/**
 * v4 claim computation.
 *
 * v3 valued each account at the best of Raketech's own sources and compared
 * that against what Adwin invoiced. Its invoicing came from `/api/raketech/
 * compare`, which matched Adwin's invoice detail against the accounts in
 * Raketech's June 2025 file only. Accounts that first appear in Raketech's
 * July 2026 report — everything created from December 2024 — were outside that
 * universe, so their invoice lines were never attached to them and v3 read
 * them as invoiced at nil. Raketech put this to Adwin on 23 September 2026 with
 * Paratiisi (18466): two lines a month, one per deal, summing to the cent to
 * what the July report records, shown in v3 as "never reported to Adwin".
 * The same defect reached every account created after the June file, and the
 * v3 matcher's brand-and-geo fallbacks had also placed lines on the wrong
 * sibling where one partner ran several accounts.
 *
 * v4 changes exactly one thing about the evidence: where the invoicing comes
 * from. Every line of Adwin's invoice detail is attributed once, to one
 * account, across the union of the June file, the Partner Account sheet and
 * the July report, by the deal code the line itself carries first and by its
 * brand only where no code identifies it (`shared/raketech/invoiceAttribution`).
 * The attribution returns the identity all = attributed + unattributed, and the
 * summary carries it, so the report can show that nothing was dropped and
 * nothing counted twice. Every row is one Raketech account id; the comparison
 * endpoint is still consumed read-only for the sources it alone holds — the
 * operator-backend sync, manual checks and the January column reference.
 *
 * Everything else — the source hierarchy, the tail on their own July report,
 * Affiliation Cloud as supporting data only, the two concessions of 9 June
 * 2026, the marks and the outstanding statement — is v3's, unchanged.
 */

import type {
  AttributionResponse,
  AccountInvoicingWire,
} from '@shared/raketech/invoiceAttribution';
import type {
  ClaimBridgeStep,
  ClaimInvoiceEntry,
  ClaimMonth,
  ClaimReference,
  ClaimRow,
  ClaimScopeTotals,
  ClaimSource,
  ClaimSummary,
  CoverageDiagnostics,
  OutstandingLine,
  OutstandingStatement,
  SourceCandidate,
} from '../types/raketechFinalReportV4.types';
import type { NewReconDataset, NewReconOffer } from '../types/raketechReconDiff.types';
import type { JuneReconAccount, JuneReconDataset } from '../types/raketechReconJune.types';

/** Last month the original reconciliation covered. */
export const RECON_END = '2024-11';

/**
 * Positions Adwin has already taken in writing, which the claim must not
 * contradict. Both were stated to Raketech on 9 June 2026, in answer to the
 * three accounts Raketech itself put forward, and both rule out a manual
 * account-level check: on Lucky7Even the manual total was withdrawn as
 * unsupported, and on Slots Palace Adwin accepted that the account is shared
 * and that it is entitled to its own campaign only. The figures stay visible;
 * they simply cannot value the account.
 */
const CONCEDED: Record<string, { exclude: ClaimSource[]; note: string }> = {
  14683: {
    exclude: ['manual_check'],
    note: 'Adwin withdrew its manual figure of EUR 121,402.93 on 9 June 2026 as unsupported by the source data; the account is valued on Raketech\'s own records.',
  },
  13354: {
    exclude: ['manual_check', 'synced'],
    note: 'Adwin accepted on 9 June 2026 that this is a shared account on which it is entitled to its own campaign earnings only, so no account-level figure of Adwin\'s own is used; the account is valued on Raketech\'s own columns.',
  },
};

/** Subset of `/api/raketech/compare`'s issue shape that v3 reads. */
export interface CompareIssue {
  type: string;
  accountId: string;
  username?: string;
  partner?: string;
  brand?: string;
  brands?: string[];
  offer?: string;
  geo?: string;
  currency?: string;

  invoiceRevenue?: number;
  postReconInvoiceRevenue?: number;
  invoiceMonthly?: { month: string; revenue: number; ftds?: number }[];
  invoiceEntries?: {
    label?: string;
    ftds?: number;
    revenue?: number;
    monthly?: { month: string; revenue: number; ftds?: number }[];
  }[];

  /** The January 2025 file, as imported into D1. Reference only in v3. */
  reconciliationEarnings?: number;
  reconciliationEarningsOriginal?: number;
  reconMonthly?: { month: string; earnings: number }[];

  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsCurrency?: string;
  adwstatsAccountId?: string;
  adwstatsDataSynced?: boolean;
  syncedMonthly?: { month: string; commission: number }[];

  manualCheckAmount?: number;
  partnerCommissionOriginal?: number;
}

export interface CompareResponse {
  issues: CompareIssue[];
  totals?: {
    actualInvoiced?: number;
    invoiced?: number;
    reconEarnings?: number;
    adwstatsSynced?: number;
  };
}

export interface V4Options {
  /** Adwin's invoice detail, placed against Raketech's accounts once each.
   *  From `/api/raketech/invoice-attribution`. */
  attribution: AttributionResponse;
  /** Ignore months after this, inclusive bound (`YYYY-MM`). */
  lastMonth: string;
  /** Apply the 5% partner margin to Raketech's gross monthly columns. */
  applyMargin: boolean;
  /** Per-account AC commission by month, keyed by Raketech account id. Only
   *  present when the user has an Affiliation Cloud session, exactly as v1.
   *  The Dec 2024 onward total is derived from this rather than passed in. */
  acMonthlyByAccountId?: Map<string, Map<string, number>>;
  /** The deal terms Raketech reported per offer, from `raketech_invoices`.
   *  Where a deal paid per acquisition and no revenue share, the revenue in
   *  the operator account was never Adwin's to earn, so backend figures are
   *  not a measure of what was owed on it. See `scripts/export-deal-basis.ts`. */
  deals?: DealBasisDataset;
}

export interface DealBasis {
  offer: string;
  rsPct: number;
  ftds: number;
  cpa: number;
  rs: number;
  total: number;
  lines: number;
  acquisitionOnly: boolean;
}

export interface DealBasisDataset {
  generatedAt: string;
  source: string;
  offerCount: number;
  acquisitionOnlyCount: number;
  offers: Record<string, DealBasis>;
}

export interface V4Result {
  rows: ClaimRow[];
  summary: ClaimSummary;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

const monthPlus = (m: string, n: number): string => {
  const [y, mm] = m.split('-').map(Number);
  const d = new Date(Date.UTC(y, mm - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/**
 * Months after the reconciled period invoiced above what Raketech's July 2026
 * report carries for them, net of booking shifts.
 *
 * Raketech's report books a month's earnings in the month the players signed
 * up; its monthly report — and Adwin's invoice on it — booked them the month
 * after. The accounts opened in May 2025 show it plainly: the report carries
 * May, the invoice carries June, and the two agree to the cent once the 5% is
 * allowed for. So a month's shortfall is first absorbed by what the report
 * carries above the invoice in the month before or after, each such surplus
 * used once; what remains is a reduction. `shifted` is the part absorbed.
 */
export function tailReductionNetOfShift(
  invoiced: Map<string, { revenue: number }>,
  theirs: Map<string, number>,
  lastMonth: string,
): { reduction: number; shifted: number } {
  const months = [...new Set([...invoiced.keys(), ...theirs.keys()])]
    .filter((m) => m > RECON_END && m <= lastMonth)
    .sort();
  const surplus = new Map<string, number>();
  for (const m of months) {
    const above = (theirs.get(m) || 0) - (invoiced.get(m)?.revenue || 0);
    if (above > 0) surplus.set(m, above);
  }
  let reduction = 0, shifted = 0;
  for (const m of months) {
    const revenue = invoiced.get(m)?.revenue || 0;
    if (revenue <= 0) continue;
    let short = revenue - (theirs.get(m) || 0);
    if (short <= 1) continue;
    for (const n of [monthPlus(m, -1), monthPlus(m, 1)]) {
      const s = surplus.get(n) || 0;
      if (s <= 0) continue;
      const take = Math.min(s, short);
      surplus.set(n, s - take);
      short -= take;
      shifted += take;
      if (short <= 0) break;
    }
    if (short > 1) reduction += short;
  }
  return { reduction, shifted };
}

export const monthKey = (m: string): string => {
  if (/^\d{4}-\d{2}$/.test(m)) return m;
  const slash = m.match(/^\d{2}\/(\d{2})\/(\d{4})$/);
  if (slash) return `${slash[2]}-${slash[1]}`;
  const names: Record<string, string> = {
    january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
    july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
  };
  const parts = m.trim().split(/\s+/);
  if (parts.length === 2) {
    const num = names[parts[0].toLowerCase()];
    if (num) return `${parts[1]}-${num}`;
  }
  return m;
};

/** AC earnings for the post-reconciliation tail, from the monthly series. */
function acTailFor(
  acMonthly: Map<string, number> | undefined,
  lastMonth: string,
): number {
  if (!acMonthly) return 0;
  let total = 0;
  for (const [month, amount] of acMonthly) {
    if (month > RECON_END && month <= lastMonth) total += amount || 0;
  }
  return r2(total);
}

/**
 * Affiliation Cloud reports commission gross of Raketech's partner margin, the
 * same way their workbook columns do. AC is carried as supporting data rather
 * than as a claim source, but it is only worth showing beside the rest if it is
 * on the same basis, so the margin is applied here too.
 */
function acOnClaimBasis(
  acMonthly: Map<string, number> | undefined,
  offers: NewReconOffer[],
  applyMargin: boolean,
  margin: MarginTerms,
): Map<string, number> | undefined {
  if (!acMonthly) return undefined;
  if (!applyMargin || !offers.some((o) => o.pa)) return acMonthly;
  const out = new Map<string, number>();
  for (const [month, amount] of acMonthly) {
    out.set(month, month <= margin.period1End ? r2(amount * (1 - margin.rate)) : amount);
  }
  return out;
}

/**
 * Invoicing for one account, from the v4 attribution.
 *
 * Every line has already been placed once, so there is nothing to de-duplicate
 * here and no header total to fall back on: an account with no attributed
 * lines was not invoiced. Months are keyed `YYYY-MM`.
 */
function invoiceByMonth(
  inv: AccountInvoicingWire | undefined,
): Map<string, { revenue: number; ftds: number }> {
  const out = new Map<string, { revenue: number; ftds: number }>();
  if (!inv) return out;
  for (const [month, m] of Object.entries(inv.monthly)) {
    out.set(monthKey(month), { revenue: m.revenue || 0, ftds: m.ftds || 0 });
  }
  return out;
}

/** The account's invoice lines by deal label, with how each was placed. */
function invoiceEntriesFor(
  inv: AccountInvoicingWire | undefined,
  lastMonth: string,
): ClaimInvoiceEntry[] {
  if (!inv) return [];
  return inv.entries.map((entry) => {
    const monthly = Object.entries(entry.monthly)
      .map(([month, m]) => ({ month: monthKey(month), ftds: m.ftds || 0, revenue: m.revenue || 0 }))
      .filter((m) => m.month <= lastMonth)
      .sort((a, b) => a.month.localeCompare(b.month));
    return {
      label: entry.label,
      ftds: monthly.reduce((s, m) => s + m.ftds, 0),
      revenue: r2(monthly.reduce((s, m) => s + m.revenue, 0)),
      monthly,
      rules: entry.rules,
      judged: entry.judged,
      split: entry.split,
      contested: entry.contested,
    };
  }).filter((e) => e.revenue !== 0 || e.monthly.length > 0);
}

function seriesByMonth(rows: { month: string }[] | undefined, pick: (r: never) => number): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of rows || []) {
    const k = monthKey(row.month);
    out.set(k, (out.get(k) || 0) + (pick(row as never) || 0));
  }
  return out;
}

/** The June workbook's credited monthly series, cut at the report's month. */
function juneMonths(account: JuneReconAccount | undefined, lastMonth: string): Map<string, number> {
  const out = new Map<string, number>();
  if (!account) return out;
  for (const [month, value] of Object.entries(account.monthly)) {
    if (month <= lastMonth) out.set(month, value);
  }
  return out;
}

const sumMap = (m: Map<string, number>) => {
  let total = 0;
  for (const v of m.values()) total += v;
  return r2(total);
};

/** Takes a share of every month, for a workbook figure split across rows. */
const scaleMap = (m: Map<string, number>, share: number) => {
  if (share === 1) return m;
  const out = new Map<string, number>();
  for (const [k, v] of m) out.set(k, r2(v * share));
  return out;
};

/** The margin terms Raketech applied to their own workbook. */
interface MarginTerms {
  rate: number;
  /** Last month the margin was taken on partner accounts (`YYYY-MM`). */
  period1End: string;
}

/** Raketech's restated earnings for one account, split at the reconciliation
 *  boundary and put on our net-of-margin basis. */
function restatedForAccount(
  offers: NewReconOffer[],
  lastMonth: string,
  applyMargin: boolean,
  margin: MarginTerms,
): { recon: number; tail: number; monthly: Map<string, number> } {
  let recon = 0;
  let tail = 0;
  const monthly = new Map<string, number>();
  for (const offer of offers) {
    const margined = applyMargin && offer.pa;
    for (const [month, gross] of Object.entries(offer.monthly)) {
      if (month > lastMonth) continue;
      // The margin stopped applying to partner accounts after the first
      // period; Raketech's own Summary reports those months gross.
      const factor = margined && month <= margin.period1End ? 1 - margin.rate : 1;
      const value = gross * factor;
      monthly.set(month, (monthly.get(month) || 0) + value);
      if (month <= RECON_END) recon += value;
      else tail += value;
    }
  }
  return { recon: r2(recon), tail: r2(tail), monthly };
}

function pickBest(candidates: SourceCandidate[]): SourceCandidate {
  let best = candidates[0];
  for (const c of candidates) {
    if (!c.available) continue;
    if (!best.available || c.amount > best.amount) best = c;
  }
  return best;
}

/**
 * Brand names as they appear in either workbook, reduced to something that
 * survives Raketech relabelling an account. Deliberately loose: a false match
 * only ever moves a month from "missing from their data" to "reported under
 * another id", which is the reading that favours them.
 */
const brandKey = (s: string): string =>
  (s || '').toLowerCase().replace(/[^a-z0-9]/g, '')
    .replace(/(casino|partners|affiliates)$/, '');

/** Every `brand|month` Raketech's own two workbooks report anything against. */
function buildBrandMonthIndex(dataset: NewReconDataset, june: JuneReconDataset): Set<string> {
  const index = new Set<string>();
  for (const offer of dataset.offers) {
    const key = brandKey(offer.brand);
    if (!key) continue;
    for (const [month, value] of Object.entries(offer.monthly || {})) {
      if (Number(value) > 0) index.add(`${key}|${month}`);
    }
  }
  for (const account of june.accounts) {
    for (const brand of account.brands || []) {
      const key = brandKey(brand);
      if (!key) continue;
      for (const [month, value] of Object.entries(account.monthly || {})) {
        if (Number(value) > 0) index.add(`${key}|${month}`);
      }
    }
  }
  return index;
}

const EMPTY_COVERAGE: CoverageDiagnostics = {
  gapMonths: 0, gapInvoiced: 0, gapReportedElsewhere: 0, gapAbsent: 0,
  partialMonths: 0, partialWedge: 0, monthLevelEarned: 0, monthLevelLift: 0,
  blankMonthOverpaid: 0, partialMonthOverpaid: 0, evidencedOverpaid: 0,
  describedInvoiced: 0, undescribedInvoiced: 0, describedInvoicedInGroup: 0,
};

/**
 * Raketech's "Reported (EUR)" column, scaled to this row's share of the account.
 *
 * This is what their monthly reports paid out on, and what Adwin invoiced
 * against at the time. Where they later credited less, the difference is a
 * write-down of their own report rather than a figure Adwin can test, so the
 * higher of the two stands until they evidence the reduction.
 *
 * The column is an account total with no month behind it. Where the credited
 * column does carry months, the shape is reused and scaled up to the reported
 * total; where it does not, the account is compared as a single figure.
 */
function reportedFor(
  june: JuneReconAccount | undefined,
  creditedMonthly: Map<string, number>,
  share: number,
): { amount: number; monthly: Map<string, number> } {
  const amount = r2((june?.reported || 0) * share);
  if (amount <= 0) return { amount: 0, monthly: new Map() };
  const credited = sumMap(creditedMonthly);
  if (credited <= 0) return { amount, monthly: new Map() };
  return { amount, monthly: scaleMap(creditedMonthly, amount / credited) };
}

/**
 * Measures what the account's figure does not rest on.
 *
 * Two different things are counted. A gap month is one Adwin invoiced and none
 * of their sources describes at all; the account reads as an overpayment for it
 * on no evidence either way. A partial month is one they describe at less than
 * half of what was invoiced. Separately, `monthLevelEarned` re-picks the best
 * source in each month rather than once for the account, which matters when one
 * workbook covers months another is silent on.
 *
 * `monthLevelEarned` is floored at the account's own chosen figure. Not every
 * source carries a month breakdown: `manual_check` is an account-level total
 * only, so summing its months would throw away evidence the account is already
 * proved on. Flooring keeps the month-level view a strict improvement.
 */
function coverageFor(
  months: ClaimMonth[],
  brands: string[],
  index: Set<string>,
  earnedTotal: number,
  invoicedTotal: number,
): CoverageDiagnostics {
  const keys = [...new Set(brands.map(brandKey).filter(Boolean))];
  const out: CoverageDiagnostics = { ...EMPTY_COVERAGE };

  let monthSum = 0;
  let monthedInvoiced = 0;
  let describedInvoiced = 0;
  for (const month of months) {
    monthSum += month.earned;
    monthedInvoiced += Math.max(0, month.invoiced);
    if (month.earned > 0) describedInvoiced += Math.max(0, month.invoiced);
    if (month.invoiced <= 0) continue;

    if (month.earned === 0) {
      out.gapMonths += 1;
      out.gapInvoiced += month.invoiced;
      if (keys.some((k) => index.has(`${k}|${month.month}`))) {
        out.gapReportedElsewhere += month.invoiced;
      } else {
        out.gapAbsent += month.invoiced;
      }
    } else if (month.earned < month.invoiced * 0.5) {
      out.partialMonths += 1;
      out.partialWedge += month.invoiced - month.earned;
    }
  }

  out.monthLevelEarned = r2(Math.max(monthSum, earnedTotal));
  out.monthLevelLift = r2(Math.max(0, out.monthLevelEarned - earnedTotal));

  // Not every source carries months: `manual_check` and `synced` are account
  // level totals. Where an account has earnings with no month behind them there
  // is nothing to line up, so the whole account is compared as it stands. An
  // account with no earnings at all is a different case — there the months are
  // exactly what is missing, and each one is tested on its own.
  const hasMonthDetail = earnedTotal <= 0 || monthSum >= earnedTotal - 1;
  out.describedInvoiced = r2(hasMonthDetail ? describedInvoiced : invoicedTotal);
  out.undescribedInvoiced = r2(Math.max(0, invoicedTotal - out.describedInvoiced));
  out.gapInvoiced = r2(out.gapInvoiced);
  out.gapReportedElsewhere = r2(out.gapReportedElsewhere);
  out.gapAbsent = r2(out.gapAbsent);
  out.partialWedge = r2(out.partialWedge);

  // Split the apparent overpayment by how much of it their own silence explains.
  // Invoicing with no month behind it counts as silence too: an unmatched deal
  // label describes nothing their file could confirm or contradict.
  const overpaid = invoicedTotal - earnedTotal;
  if (overpaid > 0) {
    const unmonthed = Math.max(0, invoicedTotal - monthedInvoiced);
    out.blankMonthOverpaid = r2(Math.min(overpaid, out.gapInvoiced + unmonthed));
    out.partialMonthOverpaid = r2(Math.min(overpaid - out.blankMonthOverpaid, out.partialWedge));
    out.evidencedOverpaid = r2(overpaid - out.blankMonthOverpaid - out.partialMonthOverpaid);
  }
  return out;
}

export function buildClaim(
  compare: CompareResponse,
  dataset: NewReconDataset,
  june: JuneReconDataset,
  options: V4Options,
): V4Result {
  const { lastMonth, applyMargin } = options;
  const margin: MarginTerms = { rate: dataset.marginRate, period1End: dataset.period1End };

  // Raketech's July 2026 workbook, grouped by the account id both reports share.
  const restatedByAccount = new Map<string, NewReconOffer[]>();
  for (const offer of dataset.offers) {
    const id = String(offer.hubId || '').trim();
    if (!id) continue;
    if (!restatedByAccount.has(id)) restatedByAccount.set(id, []);
    restatedByAccount.get(id)!.push(offer);
  }

  // Their June 2025 workbook, on the same account id.
  const juneByAccount = new Map<string, JuneReconAccount>();
  for (const account of june.accounts) {
    const id = String(account.id || '').trim();
    if (id) juneByAccount.set(id, account);
  }

  const reconMonths = dataset.months.filter((m) => m <= RECON_END && m <= lastMonth);
  const tailMonths = dataset.months.filter((m) => m > RECON_END && m <= lastMonth);
  const brandMonths = buildBrandMonthIndex(dataset, june);
  const juneThrough = june.months[june.months.length - 1] || RECON_END;

  // One row per Raketech account id. The comparison endpoint occasionally
  // splits an account across rows by brand or geo; those rows are folded back
  // together here, because the invoicing now arrives per account from the
  // attribution and their workbooks report the account once. Rows the
  // comparison could not tie to any account carry no invoicing of their own in
  // v4 — the lines they held have been placed on the accounts whose deal codes
  // they name — so they are not rows here.
  const issuesByAccount = new Map<string, CompareIssue[]>();
  for (const iss of compare.issues) {
    const id = String(iss.accountId || '').trim();
    if (!id) continue;
    if (!issuesByAccount.has(id)) issuesByAccount.set(id, []);
    issuesByAccount.get(id)!.push(iss);
  }

  // Every account any source names: the comparison, either workbook, or the
  // attribution itself (an account Adwin invoiced that Raketech reports nowhere).
  const accountIds = new Set<string>([
    ...issuesByAccount.keys(),
    ...restatedByAccount.keys(),
    ...juneByAccount.keys(),
    ...Object.keys(options.attribution.accounts),
  ]);

  const rows: ClaimRow[] = [];
  for (const accountId of accountIds) {
    const issues = issuesByAccount.get(accountId) || [];
    const offers = restatedByAccount.get(accountId) || [];
    const juneAccount = juneByAccount.get(accountId);
    const iss = mergeIssues(issues, accountId, offers, juneAccount);
    const row = buildRow(
      iss, accountId, offers, juneAccount, options, margin, juneThrough,
      options.attribution.accounts[accountId],
    );
    if (row) {
      row.newToThisReport = issues.length === 0;
      row.coverage = coverageFor(
        row.monthly, row.brands, brandMonths, row.earnedTotal, row.invoicedTotal,
      );
      rows.push(row);
    }
  }
  const duplicatesRemoved = 0;

  // Describedness, decided once per partner rather than once per account.
  //
  // Raketech books a month's earnings under a sibling account often enough that
  // the per-account test is unsafe on its own: it would set aside the invoice
  // raised under one id while the earnings for the same month stand under
  // another, and claim the difference twice. PariMatch March 2023 is the plain
  // case — EUR 55,770 invoiced under 13826, reported under 12435.
  {
    const described = new Map<string, Set<string>>();
    for (const row of rows) {
      const key = brandKey(row.partner || row.brands[0] || row.accountId) || row.accountId;
      const set = described.get(key) || new Set<string>();
      for (const m of row.monthly) if (m.earned > 0) set.add(m.month);
      described.set(key, set);
    }
    for (const row of rows) {
      const key = brandKey(row.partner || row.brands[0] || row.accountId) || row.accountId;
      const set = described.get(key) || new Set<string>();
      const monthSum = row.monthly.reduce((s, m) => s + m.earned, 0);
      const hasMonthDetail = row.earnedTotal <= 0 || monthSum >= row.earnedTotal - 1;
      if (!hasMonthDetail) {
        row.coverage.describedInvoicedInGroup = row.invoicedTotal;
        continue;
      }
      let inGroup = 0;
      for (const m of row.monthly) if (set.has(m.month)) inGroup += Math.max(0, m.invoiced);
      row.coverage.describedInvoicedInGroup = r2(inGroup);
    }
  }

  rows.sort((a, b) => a.underpaid - b.underpaid);

  return { rows, summary: summarise(rows, reconMonths, tailMonths, options.attribution) };
}

/**
 * The comparison's view of one account, as a single issue.
 *
 * Where the endpoint split an account across rows (two accounts in the data,
 * Just Casino / Let's Lucky and Ultra Casino PE / CL), the row with the most
 * invoicing is the base and the others contribute their brands and geos. The
 * operator-backend sync is summed only where it came from different AC
 * accounts; a manual check is an account-level figure and is taken once. For
 * an account the comparison never had, an empty issue carrying the workbook's
 * own names is returned, so every account goes through the same code.
 */
function mergeIssues(
  issues: CompareIssue[],
  accountId: string,
  offers: NewReconOffer[],
  juneAccount: JuneReconAccount | undefined,
): CompareIssue {
  if (issues.length === 0) {
    const first = offers[0];
    return {
      type: 'restated_only',
      accountId,
      username: juneAccount?.usernames[0] || '',
      partner: first?.partner || juneAccount?.partners[0] || '',
      brands: [...new Set([
        ...offers.map((o) => o.brand),
        ...(juneAccount?.brands || []),
      ].filter(Boolean))],
      currency: 'EUR',
    };
  }
  if (issues.length === 1) return issues[0];
  const sorted = [...issues].sort((a, b) => (b.invoiceRevenue || 0) - (a.invoiceRevenue || 0));
  const base = { ...sorted[0] };
  base.brands = [...new Set(sorted.flatMap((i) => i.brands && i.brands.length > 0 ? i.brands : [i.brand]).filter(Boolean) as string[])];
  const acIds = new Set(sorted.map((i) => i.adwstatsAccountId).filter(Boolean));
  if (acIds.size > 1) {
    base.adwstatsCommission = sorted.reduce((s, i) => s + (i.adwstatsCommission || 0), 0);
    base.adwstatsCommissionOriginal = sorted.reduce((s, i) => s + (i.adwstatsCommissionOriginal || 0), 0);
    const merged = new Map<string, number>();
    for (const i of sorted) for (const m of i.syncedMonthly || []) merged.set(m.month, (merged.get(m.month) || 0) + m.commission);
    base.syncedMonthly = [...merged].map(([month, commission]) => ({ month, commission }));
  }
  base.manualCheckAmount = Math.max(...sorted.map((i) => i.manualCheckAmount || 0));
  return base;
}

function referenceFor(june: JuneReconAccount | undefined, previouslyClaimed: number): ClaimReference {
  return {
    januaryFile: r2(june?.firstFile || 0),
    previouslyClaimed: r2(previouslyClaimed),
    raketechReported: r2(june?.reported || 0),
    juneDifference: r2(june?.difference || 0),
    juneComments: june?.comments || [],
  };
}

/**
 * What Raketech's own monthly reports say Adwin was paid on for this account:
 * acquisitions only, revenue share, or a mix. Taken from the deal codes the
 * invoicing was raised under, which is where the terms are recorded.
 */
function dealBasisFor(
  iss: CompareIssue,
  offers: NewReconOffer[],
  deals?: DealBasisDataset,
): ClaimRow['dealBasis'] {
  if (!deals) return 'unknown';
  const codes = new Set<string>();
  for (const entry of iss.invoiceEntries || []) {
    const code = String(entry.label || '').split('—')[0].trim().toLowerCase();
    if (code) codes.add(code);
  }
  if (!codes.size) {
    for (const offer of offers) {
      const code = String(offer.offer || '').trim().toLowerCase();
      if (code) codes.add(code);
    }
  }
  const found = [...codes].map((c) => deals.offers[c]).filter(Boolean);
  if (!found.length) return 'unknown';
  if (found.every((d) => d.acquisitionOnly)) return 'acquisition_only';
  if (found.some((d) => d.acquisitionOnly)) return 'mixed';
  return 'revenue_share';
}

function buildRow(
  iss: CompareIssue,
  accountId: string,
  offers: NewReconOffer[],
  juneAccount: JuneReconAccount | undefined,
  options: V4Options,
  margin: MarginTerms,
  /** Last month the June workbook covers, which bounds its account totals. */
  juneThrough: string,
  /** Adwin's invoice lines placed on this account, from the attribution. */
  attributed: AccountInvoicingWire | undefined,
): ClaimRow | null {
  const { lastMonth, applyMargin, acMonthlyByAccountId } = options;

  // Invoicing is what the attribution placed here and nothing else. There is
  // no header total to fall back on: every line has been placed exactly once,
  // so an account with none was not invoiced.
  const invoiced = invoiceByMonth(attributed);
  let invoicedRecon = 0;
  let invoicedPostRecon = 0;
  for (const [month, { revenue }] of invoiced) {
    if (month > lastMonth) continue;
    if (month <= RECON_END) invoicedRecon += revenue;
    else invoicedPostRecon += revenue;
  }

  const restated = restatedForAccount(offers, lastMonth, applyMargin, margin);
  const juneMonthly = juneMonths(juneAccount, lastMonth);
  const juneAmount = sumMap(juneMonthly);
  const reported = reportedFor(juneAccount, juneMonthly, 1);

  const syncedRaw = iss.adwstatsCommission || 0;
  const manualRaw = iss.manualCheckAmount || 0;

  // What Raketech's monthly reports paid on. This is recorded but does not cap
  // the account's value: clause 4.8 of the agreement measures the fee by what
  // the Gaming Operator pays or owes Raketech on the Partner's New Users in a
  // Partner Account, less 5% (4.2, 4.3.2, 4.7), not by the basis Raketech chose
  // to report. An account reported as acquisition-only while the operator was
  // paying revenue share on it is therefore a question for Raketech under 4.10
  // and 6.2, and is marked as one rather than written down.
  const dealBasis = dealBasisFor(iss, offers, options.deals);

  const candidates: SourceCandidate[] = [
    {
      source: 'june_recon',
      amount: juneAmount,
      marginApplied: juneAccount?.margin ?? false,
      available: juneAmount > 0,
    },
    {
      source: 'june_reported',
      amount: reported.amount,
      marginApplied: juneAccount?.margin ?? false,
      // The column is a total for the whole reconciled period, so it can only
      // be compared once the cutoff has reached the end of that period.
      available: reported.amount > 0 && lastMonth >= juneThrough,
    },
    {
      source: 'synced',
      amount: r2(syncedRaw * 0.95),
      originalAmount: iss.adwstatsCommissionOriginal,
      originalCurrency: iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' ? iss.adwstatsCurrency : undefined,
      marginApplied: true,
      available: syncedRaw > 0,
    },
    {
      source: 'manual_check',
      amount: r2(manualRaw * 0.95),
      originalAmount: iss.partnerCommissionOriginal,
      originalCurrency: iss.currency && iss.currency !== 'EUR' ? iss.currency : undefined,
      marginApplied: true,
      available: manualRaw > 0,
    },
    {
      source: 'raketech_restated',
      amount: restated.recon,
      marginApplied: applyMargin && offers.some((o) => o.pa),
      available: restated.recon > 0,
    },
  ];

  const conceded = accountId ? CONCEDED[accountId] : undefined;
  if (conceded) {
    for (const c of candidates) {
      if (!conceded.exclude.includes(c.source)) continue;
      c.available = false;
      c.withheld = conceded.note;
    }
  }

  const best = pickBest(candidates);
  const earnedReconPeriod = best.available ? best.amount : 0;

  // Their own workbook is the tail evidence. Affiliation Cloud is reported
  // beside it and never values the account: AC returns commission per report
  // item and the item names every affiliate account attached to it, so the same
  // figure lands on each of them. Two accounts on one offer therefore read the
  // same tail, which is a duplicate rather than a second earning.
  const acMonthly = acOnClaimBasis(
    accountId ? acMonthlyByAccountId?.get(accountId) : undefined,
    offers, applyMargin, margin,
  );
  const acTail = acTailFor(acMonthly, lastMonth);
  const earnedTail = lastMonth > RECON_END ? restated.tail : 0;

  const earnedTotal = r2(earnedReconPeriod + earnedTail);
  const invoicedTotal = r2(invoicedRecon + invoicedPostRecon);

  if (earnedTotal <= 0 && invoicedTotal <= 0) return null;

  // What the same account would have looked like without their restatement,
  // which is what makes the restatement's contribution quantifiable.
  const withoutRestated = pickBest(candidates.filter((c) => c.source !== 'raketech_restated'));
  const earnedWithout = r2((withoutRestated.available ? withoutRestated.amount : 0)
    + (lastMonth > RECON_END ? acTail : 0));

  // And what v2 came to, when the disowned January column could still win.
  const januaryValue = r2(iss.reconciliationEarnings || 0);
  const earnedOnJanuary = r2(Math.max(earnedReconPeriod, januaryValue) + earnedTail);

  const underpaid = r2(invoicedTotal - earnedTotal);
  const underpaidWithout = r2(invoicedTotal - earnedWithout);

  // What their own report standing above their own credit is worth here.
  const withoutReported = pickBest(candidates.filter((c) => c.source !== 'june_reported'));
  const reportedLift = r2(Math.max(0, earnedReconPeriod
    - (withoutReported.available ? withoutReported.amount : 0)));

  // The reduction inside Raketech's own file: what its monthly reports paid out
  // on, against what the same file credits the account with. This is a fact
  // about their document and does not depend on which source values the row,
  // so an account written down to nil is still a written-down account even
  // where Adwin values it from elsewhere. Only comparable once the cutoff
  // covers the period the reported column totals.
  const reportedVsCredited = lastMonth >= juneThrough
    ? r2(Math.max(0, reported.amount - juneAmount))
    : 0;

  // The same write-down on the later months. Adwin invoiced these on
  // Raketech's monthly reports; where the July 2026 report now carries less
  // for the month than was invoiced on it, Raketech has reduced its own
  // figure. Adwin invoices net of the 5% and the report is gross from May
  // 2025, so the ordinary month runs the other way and contributes nothing.
  // A month's shortfall is first netted against what Raketech carries above
  // the invoice in the months either side: its report books a month's
  // earnings where its monthly report booked them a month later, and that is
  // a booking shift, not a reduction.
  const tailWrittenDown = tailReductionNetOfShift(invoiced, restated.monthly, lastMonth).reduction;

  // Raketech's own "Adwin earnings" column for the account, all months. Their
  // Summary sheet takes no margin after April 2025; this column does, on most
  // accounts. The claim follows the Summary and carries the column beside it.
  const julyStatedAdwinEarnings = r2(offers.reduce((s, o) => s + (o.adwinEarnings || 0), 0));

  const invMap = new Map([...invoiced].filter(([m]) => m <= lastMonth));
  const januaryMap = seriesByMonth(iss.reconMonthly, (r: { earnings: number }) => r.earnings);
  // Synced months carry the same 5% partner margin as the account-level figure,
  // otherwise the drilldown would not add up to the row above it.
  const syncedMap = seriesByMonth(iss.syncedMonthly, (r: { commission: number }) => r.commission * 0.95);

  return {
    // One account id can carry several of Adwin's deal groups, and each is its
    // own row, so the offer and geo have to be part of the key or the two rows
    // become one wherever rows are held by key.
    // One row per Raketech account id, so the id is the key.
    key: `acc|${accountId}`,
    accountId,
    acIds: [...new Set([
      ...(iss.adwstatsAccountId ? [String(iss.adwstatsAccountId)] : []),
      ...offers.map((o) => String(o.acId)).filter(Boolean),
    ])],
    username: iss.username || '',
    partner: iss.partner || '',
    brands: iss.brands && iss.brands.length > 0
      ? iss.brands
      : [...new Set([iss.brand, ...offers.map((o) => o.brand)].filter(Boolean) as string[])],
    geos: iss.geo ? [iss.geo] : [],
    currency: iss.currency || 'EUR',
    newToThisReport: false,
    missingFromAdwstats: !iss.adwstatsAccountId,
    inJuneRecon: Boolean(juneAccount),
    invoicedRecon: r2(invoicedRecon),
    invoicedPostRecon: r2(invoicedPostRecon),
    invoicedTotal,
    candidates,
    reference: { ...referenceFor(juneAccount, januaryValue), julyStatedAdwinEarnings },
    dealBasis,
    concession: conceded?.note,
    chosenSource: best.available ? best.source : 'june_recon',
    earnedReconPeriod,
    earnedTail: r2(earnedTail),
    earnedTailFromAc: r2(acTail),
    earnedTotal,
    underpaid,
    underpaidPct: earnedTotal > 0 ? r2((underpaid / earnedTotal) * 100) : 0,
    attributableToRestatement: r2(Math.max(0, underpaidWithout - underpaid)),
    reportedLift,
    reportedVsCredited,
    tailWrittenDown: r2(tailWrittenDown),
    januaryColumnEffect: r2(Math.max(0, earnedOnJanuary - earnedTotal)),
    offers,
    monthly: buildMonths({
      invoiced: invMap,
      juneRecon: juneMonthly,
      juneReported: reported.monthly,
      januaryFile: januaryMap,
      synced: syncedMap,
      ac: acMonthly || new Map(),
      restated: restated.monthly,
    }, lastMonth),
    // Filled by the caller, which holds the index of both workbooks.
    coverage: { ...EMPTY_COVERAGE },
    invoiceEntries: invoiceEntriesFor(attributed, lastMonth),
  };
}

interface MonthSeries {
  invoiced: Map<string, { revenue: number; ftds: number }>;
  juneRecon: Map<string, number>;
  /** Their credited months scaled to their own reported total. */
  juneReported: Map<string, number>;
  januaryFile: Map<string, number>;
  synced: Map<string, number>;
  ac: Map<string, number>;
  restated: Map<string, number>;
}

/**
 * Assembles the month-by-month view of an account.
 *
 * Every series is reported, but only the ones the account-level figure was
 * chosen from decide `earned`: up to the reconciliation boundary that is the
 * June workbook, our own sync and their restatement, and after it their
 * restatement alone. The January column and Affiliation Cloud are reported and
 * never counted. Reporting a series is not the same as claiming on it.
 */
function buildMonths(series: MonthSeries, lastMonth: string): ClaimMonth[] {
  const { invoiced, juneRecon, juneReported, januaryFile, synced, ac, restated } = series;
  const months = [...new Set([
    ...invoiced.keys(), ...juneRecon.keys(), ...juneReported.keys(), ...januaryFile.keys(),
    ...synced.keys(), ...ac.keys(), ...restated.keys(),
  ])]
    .filter((m) => /^\d{4}-\d{2}$/.test(m) && m <= lastMonth)
    .sort();

  return months.map((month) => {
    const inv = invoiced.get(month);
    const invRevenue = r2(inv?.revenue || 0);
    const rest = r2(restated.get(month) || 0);
    const jun = r2(juneRecon.get(month) || 0);
    const rep = r2(juneReported.get(month) || 0);
    const jan = r2(januaryFile.get(month) || 0);
    const syn = r2(synced.get(month) || 0);
    const acAmount = r2(ac.get(month) || 0);

    const best = month <= RECON_END
      ? Math.max(jun, rep, syn, rest)
      : rest;

    return {
      month,
      invoiced: invRevenue,
      ftds: inv?.ftds || 0,
      earned: r2(best),
      juneRecon: jun,
      juneReported: rep,
      januaryFile: jan,
      synced: syn,
      ac: acAmount,
      restated: rest,
      underpaid: r2(invRevenue - best),
      unsupported: best === 0 && invRevenue > 0,
    };
  });
}

function summarise(
  rows: ClaimRow[],
  reconMonths: string[],
  tailMonths: string[],
  /** The attribution the invoicing came from, for its own accounting. */
  attribution: AttributionResponse,
): ClaimSummary {
  const bySource = {
    june_recon: { accounts: 0, earned: 0, underpaid: 0 },
    june_reported: { accounts: 0, earned: 0, underpaid: 0 },
    synced: { accounts: 0, earned: 0, underpaid: 0 },
    manual_check: { accounts: 0, earned: 0, underpaid: 0 },
    raketech_restated: { accounts: 0, earned: 0, underpaid: 0 },
  } as Record<ClaimSource, { accounts: number; earned: number; underpaid: number }>;

  let invoicedTotal = 0, earnedTotal = 0, underpaidTotal = 0, restatementEffect = 0;
  let januaryEffect = 0;
  let underpaidGross = 0, overpaidGross = 0, underpaidAccounts = 0, overpaidAccounts = 0;
  let unverifiableEarnings = 0, unverifiableUnderpaid = 0;
  let accountsNewToReport = 0, accountsMissingFromAdwstats = 0, accountsInJuneRecon = 0;
  let reportedWriteDown = 0, reportedWriteDownAccounts = 0, reportedWriteDownCommented = 0;

  const coverage = {
    ...EMPTY_COVERAGE,
    accountsWithGaps: 0,
    gapOnOverpaidAccounts: 0,
    gapOnUnderpaidAccounts: 0,
  };
  // Raketech moves an account between ids between reports; grouping the rows
  // back up puts the two halves of such a move on the same line, where they
  // cancel instead of showing as an overpayment and a shortfall at once.
  const byBrand = new Map<string, { inv: number; earn: number }>();
  const byPartner = new Map<string, { inv: number; earn: number }>();
  const addTo = (m: Map<string, { inv: number; earn: number }>, key: string, row: ClaimRow) => {
    const e = m.get(key) || { inv: 0, earn: 0 };
    e.inv += row.invoicedTotal;
    e.earn += row.earnedTotal;
    m.set(key, e);
  };

  for (const row of rows) {
    addTo(byBrand, brandKey(row.brands[0] || row.partner || row.accountId) || row.accountId, row);
    addTo(byPartner, brandKey(row.partner || row.brands[0] || row.accountId) || row.accountId, row);

    coverage.gapMonths += row.coverage.gapMonths;
    coverage.gapInvoiced += row.coverage.gapInvoiced;
    coverage.gapReportedElsewhere += row.coverage.gapReportedElsewhere;
    coverage.gapAbsent += row.coverage.gapAbsent;
    coverage.partialMonths += row.coverage.partialMonths;
    coverage.partialWedge += row.coverage.partialWedge;
    coverage.monthLevelEarned += row.coverage.monthLevelEarned;
    coverage.monthLevelLift += row.coverage.monthLevelLift;
    coverage.blankMonthOverpaid += row.coverage.blankMonthOverpaid;
    coverage.partialMonthOverpaid += row.coverage.partialMonthOverpaid;
    coverage.evidencedOverpaid += row.coverage.evidencedOverpaid;
    coverage.describedInvoiced += row.coverage.describedInvoiced;
    coverage.undescribedInvoiced += row.coverage.undescribedInvoiced;
    coverage.describedInvoicedInGroup += row.coverage.describedInvoicedInGroup;
    // Measured on their file's own reduction rather than on how much of the
    // claim leans on it, so an account they wrote down and Adwin values from
    // the operator backend still counts as written down.
    if (row.reportedVsCredited > 1) {
      reportedWriteDown += row.reportedVsCredited;
      reportedWriteDownAccounts += 1;
      if (row.reference.juneComments.length > 0) reportedWriteDownCommented += row.reportedVsCredited;
    }
    if (row.coverage.gapMonths > 0) {
      coverage.accountsWithGaps += 1;
      if (row.underpaid > 1) coverage.gapOnOverpaidAccounts += row.coverage.gapInvoiced;
      else coverage.gapOnUnderpaidAccounts += row.coverage.gapInvoiced;
    }

    invoicedTotal += row.invoicedTotal;
    earnedTotal += row.earnedTotal;
    underpaidTotal += row.underpaid;
    // Kept apart on purpose: netting these together is what lets one account's
    // overpayment discharge another account's shortfall.
    if (row.underpaid < -1) { underpaidGross += row.underpaid; underpaidAccounts += 1; }
    if (row.underpaid > 1) { overpaidGross += row.underpaid; overpaidAccounts += 1; }
    restatementEffect += row.attributableToRestatement;
    januaryEffect += row.januaryColumnEffect;
    if (row.newToThisReport) accountsNewToReport += 1;
    if (row.inJuneRecon) accountsInJuneRecon += 1;
    if (row.missingFromAdwstats) {
      accountsMissingFromAdwstats += 1;
      unverifiableEarnings += row.earnedTotal;
      if (row.underpaid < 0) unverifiableUnderpaid += row.underpaid;
    }
    const b = bySource[row.chosenSource];
    b.accounts += 1;
    b.earned += row.earnedTotal;
    b.underpaid += row.underpaid;
  }

  for (const key of Object.keys(bySource) as ClaimSource[]) {
    bySource[key].earned = r2(bySource[key].earned);
    bySource[key].underpaid = r2(bySource[key].underpaid);
  }

  for (const key of ['gapInvoiced', 'gapReportedElsewhere', 'gapAbsent', 'partialWedge',
    'monthLevelEarned', 'monthLevelLift', 'gapOnOverpaidAccounts', 'gapOnUnderpaidAccounts',
    'blankMonthOverpaid', 'partialMonthOverpaid', 'evidencedOverpaid',
    'describedInvoiced', 'undescribedInvoiced', 'describedInvoicedInGroup'] as const) {
    coverage[key] = r2(coverage[key]);
  }

  // Invoice detail is imported a month at a time and lags the workbooks. Where
  // the claim runs past it, earnings stand with nothing deducted against them,
  // and the invoice that covers those months is claimed separately as unpaid.
  // Both are the same money, so the overlap is measured rather than left in.
  let invoiceDataThrough = '';
  for (const row of rows) {
    for (const m of row.monthly) {
      if (m.invoiced > 0 && m.month > invoiceDataThrough) invoiceDataThrough = m.month;
    }
  }
  let earningsBeyondInvoiceData = 0;
  for (const row of rows) {
    for (const m of row.monthly) {
      if (invoiceDataThrough && m.month > invoiceDataThrough) earningsBeyondInvoiceData += m.earned;
    }
  }

  const groupGross = (m: Map<string, { inv: number; earn: number }>) => {
    let underpaidGross = 0, overpaidGross = 0;
    for (const g of m.values()) {
      const diff = g.inv - g.earn;
      if (diff < -1) underpaidGross += diff;
      if (diff > 1) overpaidGross += diff;
    }
    return { underpaidGross: r2(underpaidGross), overpaidGross: r2(overpaidGross), groups: m.size };
  };

  // The same three scopes on the month-level earnings, so choosing to state the
  // claim gross does not silently drop back to the account-level figure.
  const scopeTotals = (
    earnedOf: (row: ClaimRow) => number,
    invoicedOf: (row: ClaimRow) => number = (row) => row.invoicedTotal,
  ): ClaimScopeTotals => {
    let net = 0, netEvidenced = 0, byAccount = 0;
    const partners = new Map<string, { raw: number; evidenced: number }>();
    for (const row of rows) {
      const diff = invoicedOf(row) - earnedOf(row);
      // An overpayment nets only as far as their file evidences the months it
      // rests on; the rest of it is their silence, not Adwin's overbilling.
      const evidenced = diff > 0 ? Math.min(diff, row.coverage.evidencedOverpaid) : diff;
      net += diff;
      netEvidenced += evidenced;
      if (diff < -1) byAccount += diff;
      const key = brandKey(row.partner || row.brands[0] || row.accountId) || row.accountId;
      const g = partners.get(key) || { raw: 0, evidenced: 0 };
      g.raw += diff;
      g.evidenced += evidenced;
      partners.set(key, g);
    }
    let byPartner = 0, byPartnerEvidenced = 0;
    for (const g of partners.values()) {
      if (g.raw < -1) byPartner += g.raw;
      if (g.evidenced < -1) byPartnerEvidenced += g.evidenced;
    }
    return {
      net: r2(-net),
      netEvidenced: r2(-netEvidenced),
      byPartner: r2(-byPartner),
      byPartnerEvidenced: r2(-byPartnerEvidenced),
      byAccount: r2(-byAccount),
    };
  };

  let unresolvedOnShortfall = 0;
  for (const row of rows) {
    if (row.underpaid < -1) unresolvedOnShortfall += row.coverage.gapInvoiced + row.coverage.partialWedge;
  }

  // What the deduplication took out, and a check that it took all of it: after
  // it, no deal label carries the same amount in the same month on two rows.
  let stillDuplicated = 0;
  const seenLine = new Set<string>();
  for (const row of rows) {
    for (const e of row.invoiceEntries) {
      const label = String(e.label).split('—')[0].trim().toUpperCase();
      if (!label) continue;
      for (const m of e.monthly) {
        if (m.revenue <= 0) continue;
        const key = `${label}|${m.month}|${m.revenue.toFixed(2)}`;
        if (seenLine.has(key)) stillDuplicated += m.revenue;
        else seenLine.add(key);
      }
    }
  }

  return {
    reconMonths,
    tailMonths,
    accounts: rows.length,
    accountsNewToReport,
    accountsMissingFromAdwstats,
    accountsInJuneRecon,
    invoicedTotal: r2(invoicedTotal),
    earnedTotal: r2(earnedTotal),
    underpaidTotal: r2(underpaidTotal),
    underpaidGross: r2(underpaidGross),
    overpaidGross: r2(overpaidGross),
    underpaidAccounts,
    overpaidAccounts,
    underpaidWithoutRestatement: r2(underpaidTotal + restatementEffect),
    restatementEffect: r2(restatementEffect),
    // The January column could only ever raise an account's earnings, so its
    // effect on the claim is the shortfall v2 reported and v3 does not.
    underpaidOnJanuaryColumn: r2(underpaidTotal - januaryEffect),
    januaryColumnEffect: r2(januaryEffect),
    bySource,
    unverifiableEarnings: r2(unverifiableEarnings),
    unverifiableUnderpaid: r2(unverifiableUnderpaid),
    coverage,
    grouped: { byBrand: groupGross(byBrand), byPartner: groupGross(byPartner) },
    band: {
      stated: scopeTotals((row) => row.earnedTotal),
      monthLevel: scopeTotals((row) => row.coverage.monthLevelEarned),
      likeForLike: scopeTotals(
        (row) => row.earnedTotal,
        (row) => row.coverage.describedInvoicedInGroup,
      ),
      undescribedInvoiced: r2(invoicedTotal - coverage.describedInvoicedInGroup),
      unresolvedCoverage: r2(coverage.gapInvoiced + coverage.partialWedge),
      unresolvedRemapping: r2(coverage.gapReportedElsewhere),
      unresolvedOnShortfallAccounts: r2(unresolvedOnShortfall),
    },
    invoiceDataThrough,
    earningsBeyondInvoiceData: r2(earningsBeyondInvoiceData),
    duplicateInvoiceRevenueRemoved: 0,
    duplicateInvoiceRevenueRemaining: r2(stillDuplicated),
    describedInvoicedTotal: r2(coverage.describedInvoicedInGroup),
    reportedWriteDown: r2(reportedWriteDown),
    reportedWriteDownAccounts,
    reportedWriteDownCommented: r2(reportedWriteDownCommented),
    julyStatedColumn: (() => {
      let theirs = 0, claimBasis = 0, accountsMargined = 0;
      for (const row of rows) {
        const t = row.reference.julyStatedAdwinEarnings || 0;
        if (row.offers.length === 0) continue;
        // The claim's basis for the same account over every month their report
        // carries: their gross, less 5% on Partner Accounts through April 2025.
        const ours = row.monthly.reduce((s, m) => s + (m.restated || 0), 0);
        theirs += t;
        claimBasis += ours;
        if (ours - t > 1) accountsMargined += 1;
      }
      return { theirs: r2(theirs), claimBasis: r2(claimBasis), difference: r2(claimBasis - theirs), accountsMargined };
    })(),
    attribution: {
      lines: attribution.lineCount,
      all: attribution.conservation.all,
      attributed: attribution.conservation.attributed,
      unattributed: attribution.conservation.unattributed,
      balanced: attribution.conservation.balanced,
      unattributedLines: attribution.unattributed.length,
      judgedLines: attribution.judged.length,
      judgedTotal: r2(attribution.judged.reduce((t, l) => t + l.total, 0)),
      splitLines: attribution.split?.lines ?? 0,
      splitTotal: r2(attribution.split?.revenue ?? 0),
      rules: attribution.ruleCounts,
    },
  };
}

/**
 * The chain from Raketech's stated position to this report's claim, so the
 * difference between the two bottom lines is itemised rather than asserted.
 */
export function buildClaimBridge(
  dataset: NewReconDataset,
  june: JuneReconDataset,
  summary: ClaimSummary,
): ClaimBridgeStep[] {
  const c = dataset.controls;
  const steps: ClaimBridgeStep[] = [];

  steps.push({
    label: 'Underpayment Raketech states in their new report',
    amount: c.underpaymentAfterFees ?? 0,
    kind: 'figure',
    theirFigure: true,
    note: 'Summary sheet, after their own transaction-fee deduction.',
  });

  steps.push({
    label: 'Transaction fees they deduct from what they owe',
    amount: c.transactionFees,
    kind: 'addition',
    note: 'Added back because the fees are disputed: their own Finance ledger does '
      + 'not support the split they state between the two periods.',
  });

  steps.push({
    label: 'Their difference before fees',
    amount: c.difference ?? 0,
    kind: 'subtotal',
    theirFigure: true,
    note: 'Their own figure, and the sum of the two lines above.',
  });

  steps.push({
    label: 'Their headline earnings versus their own per-offer column',
    amount: c.headlineVsPerOfferGap,
    kind: 'figure',
    note: 'They add second-period earnings gross in the headline but margined in the '
      + 'per-offer column. Stated at their higher headline basis, which favours this claim.',
  });

  steps.push({
    label: 'Under-reporting their own June 2025 workbook records',
    amount: june.controls.underReported.amount,
    kind: 'figure',
    theirFigure: true,
    note: `Across ${june.controls.underReported.rows} rows of that workbook, before it nets `
      + `them against ${june.controls.overReported.rows} rows of over-reporting worth `
      + `EUR ${june.controls.overReported.amount.toLocaleString('en-US')} to reach one bottom line.`,
  });

  steps.push({
    label: 'Earnings they report but never invoiced',
    amount: r2(c.statedTotalEarnings - Math.abs(c.totalInvoiced ?? 0)),
    kind: 'figure',
    theirFigure: true,
    note: 'Their stated earnings less their stated invoiced total.',
  });

  steps.push({
    label: 'Invoiced but never paid',
    amount: r2(Math.abs(c.totalInvoiced ?? 0) - (c.totalPaid ?? 0)),
    kind: 'figure',
    theirFigure: true,
    note: 'Their stated invoiced total less their stated paid total.',
  });

  steps.push({
    label: 'This report: accounts invoiced below what they earned',
    amount: Math.abs(summary.underpaidGross),
    kind: 'result',
    note: `${summary.underpaidAccounts} of ${summary.accounts} accounts, each valued at the `
      + 'strongest evidence available. This is the claim: an account Raketech overpaid does '
      + 'not discharge a different account it underpaid.',
  });

  steps.push({
    label: 'Accounts invoiced above what they earned',
    amount: summary.overpaidGross,
    kind: 'figure',
    note: `${summary.overpaidAccounts} accounts, stated rather than netted away. Raketech's own `
      + 'bottom line nets the two together, which is what separates their figure from this one.',
  });

  steps.push({
    label: 'Net of the two, on their netting basis',
    amount: Math.abs(summary.underpaidTotal),
    kind: 'subtotal',
    note: 'What the same accounts come to if overpayments are allowed to cancel shortfalls, '
      + 'for comparison with their bottom line.',
  });

  steps.push({
    label: 'of which exists only because they restated',
    amount: summary.restatementEffect,
    kind: 'figure',
    note: 'Shortfall that appears only when their July 2026 figures are used in place of '
      + 'what we could otherwise evidence. This part is proved by their own admission.',
  });

  steps.push({
    label: 'Removed with the January 2025 column',
    amount: summary.januaryColumnEffect,
    kind: 'deduction',
    note: 'Shortfall v2 carried because it valued accounts on "New earnings 1st file", the '
      + 'January 2025 file Raketech disowned on 15 August 2025. v3 values those accounts on '
      + 'the June workbook\'s credited column instead, and this is what that costs the claim.',
  });

  return steps;
}

/**
 * Amounts outside the commission calculation, each tied to a document.
 *
 * These are held here rather than derived because they come from the agreement
 * and from Adwin's own invoices, not from either workbook. Every one of them
 * has been read off the source: the fee lines from the invoice PDFs, the
 * credits from invoice 20318, and the unpaid balance from Raketech's own
 * Finance ledger.
 */
export const SETTLEMENT_FACTS = {
  /** Clause 4.3.1: a flat fee of EUR 200,000 payable within twelve months of
   *  signature. Signed 22 June 2024, so due 22 June 2025. It sits in clause 4.3
   *  alongside the Net Revenue, CPA and CPC fees, and is additional to them. */
  flatFeeContract: 200000,
  flatFeeDueBy: '2025-06-22',
  /** "Affiliate marketing fee" lines, invoices 19926 (16 Sep 2024) through
   *  20146 (30 Jun 2025). Eleven lines. Independently corroborated: Raketech's
   *  own ledger differs from its "Excluding Fee" column by the same amount. */
  flatFeeInvoiced: 139396.48,
  /** Five of those eleven, invoices 20033 (17 Mar 2025) to 20146 (30 Jun 2025),
   *  credited back on invoice 20318. They begin the month after Raketech
   *  proposed on 20 Feb 2025 to repay the commission error over twelve months,
   *  which is what they were: part payment of the confirmed underpayment,
   *  billed under the fee label. They therefore never reached the flat fee. */
  feeLinesCreditedAsPartPayment: 52024.53,
  /** Flat fee actually retained: the six lines Sep 2024 to Feb 2025 that were
   *  not credited back. 139,396.48 less 52,024.53. */
  flatFeeRetained: 87371.95,
  /** "Reconciliation fee" lines, invoices 20170 (Jul 2025) through 20317
   *  (Apr 2026). Ten lines. These are the 5% Raketech agreed on 9 May 2025 not
   *  to retain until EUR 142,483 of transaction cost had been compensated. */
  reconciliationFeeInvoiced: 23146.37,
  /** The transaction and conversion cost Raketech accepted on 9 May 2025 it
   *  would compensate in full before charging its share again. Johan Svensson,
   *  in writing: from May 2025 Raketech takes no fee until the full amount has
   *  been compensated, on new business and the historical accounts alike. */
  transactionFeeToCompensate: 142483,
  /** First month the waiver ran from. */
  marginWaivedFrom: '2025-05',
  /** Invoice 20023, paid 21 March 2025 per their ledger: "Initial payment
   *  according to historical discrepancy report." */
  settlementPaid: 44224.93,
  /** Nothing. Invoices 20331 and 20332 — EUR 34,630.48 for May 2026 and
   *  EUR 34,955.74 for June 2026, both a single commission line with no fee line
   *  inside them — were settled together on 31 July 2026 in one transfer of
   *  EUR 69,586.22, referenced "SETT/ INV 20331 / INV 20332". Held at zero
   *  rather than deleted so the statement states the position on them. */
  unpaidInvoices: 0,
  /** What that transfer settled, for the note on the line. */
  invoicesSettled: { amount: 69586.22, on: '31 July 2026', invoices: '20331 and 20332' },
  /** The same two, by the month they bill, so the tie-out against Adwin's
   *  billing can follow the claim's cutoff. `commissionBilled` below stops at
   *  invoice 20317 and April 2026, which is where the register ended when it
   *  was reconciled to Raketech's ledger. */
  commissionBilledLater: { '2026-05': 34630.48, '2026-06': 34955.74 } as Record<string, number>,
  /** Invoice 20318 as issued. */
  invoice20318Gross: 959318.00,
  invoice20318Credits: 119395.83,
  /** Commission Adwin actually billed over the period: 81 invoices, headers net
   *  of the fee lines inside them, excluding invoice 20023 (a settlement) and
   *  20318 (this claim). Includes the EUR 131,000 of invoice 19878 — which
   *  Raketech's own ledger records as "Publisher Costs 07/2024 W2 / Outstanding
   *  from error" and shows settled on 23 August 2024 — re-issued as 20090 after
   *  19878 was credited; v3 left it out as a bookkeeping entry, but the
   *  commission behind it was billed and paid. Raketech's Finance ledger and
   *  Adwin's register agree on every invoice but two from March 2023, where the
   *  ledger is EUR 17,851.49 higher. */
  commissionBilled: 18869419.98,
  /** Invoice 20317, April 2026: in Adwin's register, not yet in Raketech's
   *  ledger extract. Used by the month-by-month tie-out. */
  invoice20317: 36409.40,
} as const;

/**
 * The 5% Raketech stopped taking from May 2025, on its own gross figures for
 * the months the claim covers. That waiver was the mechanism for compensating
 * the EUR 142,483 of transaction cost, and the claim values those months gross
 * on the strength of it, so this is the part of the compensation already
 * delivered or claimed through the commission shortfall. The rest is claimed
 * as its own head; the two together claim the EUR 142,483 exactly once.
 */
export function marginWaivedSince(rows: Pick<ClaimRow, 'monthly'>[], lastMonth: string): number {
  let gross = 0;
  for (const r of rows) {
    for (const m of r.monthly) {
      if (m.month >= SETTLEMENT_FACTS.marginWaivedFrom && m.month <= lastMonth) gross += m.restated || 0;
    }
  }
  return r2(gross * 0.05);
}

export interface OutstandingOptions {
  /** The commission shortfall being claimed, as a positive number. */
  commissionShortfall: number;
  /** `marginWaivedSince(rows, lastMonth)`: the part of the EUR 142,483 already
   *  delivered or claimed through the 5% waiver. Omit to leave the transaction
   *  cost head out of the statement. */
  marginWaived?: number;
  /**
   * How to read the EUR 52,024.53 of fee lines credited on invoice 20318.
   *
   * `part_payment` is the position: those five lines were Raketech repaying the
   * confirmed underpayment in instalments, billed under the fee label, so the
   * credit on 20318 was right and the money comes off the commission claim.
   * `flat_fee` reads them instead as clause 4.3.1 instalments, in which case
   * the credit discharged a debt they never related to and the claim keeps
   * them. Either way the flat fee balance is the same, because on the first
   * reading the flat fee never received the money and on the second it received
   * it and gave it straight back.
   */
  creditedFeeLines: 'part_payment' | 'flat_fee';
  /** The invoicing the claim deducts from earnings. Where this exceeds what
   *  Adwin actually billed, the claim has deducted invoices that were never
   *  raised, and the difference is added back. This is deliberately the figure
   *  the claim deducts, not the underlying detail: where a line is attached to
   *  two accounts the claim over-deducts, and adding the difference back is
   *  what corrects it. Omit to leave the line out. */
  claimInvoiced?: number;
  /** Last month `claimInvoiced` covers. Invoices raised after the register was
   *  reconciled are added to the billed side so the two cover the same ground. */
  invoicedThrough?: string;
  /** The reported-but-never-billed figure from the month-by-month tie-out on
   *  Raketech's own ledger (`buildBillingTieOut`). When given it replaces the
   *  register-based difference, so the statement and the table agree. */
  neverBilled?: number;
  /**
   * Whether the commission shortfall above nets overpaid accounts against
   * short ones. The shortfall is Raketech's figure less the report lines; the
   * never-billed head is the report lines less what was billed; together they
   * are Raketech's figure less what was billed, exactly — but only when both
   * directions are netted. On a shortfalls-only scope the accounts where the
   * report lines exceed Raketech's figure are set aside, and the part of the
   * never-billed head that falls on those accounts would otherwise be claimed
   * while the shortfall side already treats them as invoiced above their
   * figure. That part is `neverBilledOverlap`, from `allocateNeverBilled`, and
   * is taken out on those scopes. Defaults to false.
   */
  scopeNets?: boolean;
  /** The part of the never-billed head that falls on accounts whose report
   *  lines already exceed Raketech's figure, on the basis in use. */
  neverBilledOverlap?: number;
  /** How many accounts that touches, for the note. */
  neverBilledOverlapAccounts?: number;
  /** Earnings the claim carries in months no invoice detail has been imported
   *  for. Invoices 20331 and 20332 bill those same months and are claimed as
   *  unpaid, so this comes off to stop the two overlapping. */
  earningsBeyondInvoiceData?: number;
}

export interface BillingTieOutMonth {
  month: string;
  /** What Raketech's monthly reports said, i.e. what the claim deducts. */
  reported: number;
  /** What Adwin billed for the month, on Raketech's own Finance ledger. */
  billed: number;
  invoices: string[];
  difference: number;
  cumulative: number;
}

export interface BillingTieOut {
  months: BillingTieOutMonth[];
  totalReported: number;
  totalBilled: number;
  /** Reported and invoiced on but never billed, across the whole span. */
  neverBilled: number;
  /** Invoices in their ledger that name no period, and where they were placed. */
  placed: { invoice: string; month: string; amount: number; why: string }[];
}

/**
 * Adwin's invoicing against Raketech's own ledger of it, month by month.
 *
 * Raketech's July 2026 file carries its Finance ledger of Adwin's invoices,
 * each labelled with the period it bills ("Publisher Costs 11/2023 - Adweb").
 * That is the mapping the invoice register lacks, and it lets the monthly
 * report detail the claim deducts be set against what was actually billed for
 * the same month, on Raketech's own record. Where the two part, that is where
 * commission was reported, invoiced on, and never billed in full.
 *
 * Four of their entries name no period and are placed on what they are: the
 * two round advances of January and June 2024, invoice 20332 for June 2026,
 * and 20023, which is a settlement and not commission and is left out. 20317
 * is not in their extract yet and is taken from Adwin's register.
 */
export function buildBillingTieOut(dataset: NewReconDataset, rows: ClaimRow[], lastMonth: string): BillingTieOut {
  const entries = ((dataset as unknown as { finance?: { entries?: Array<Record<string, unknown>> } }).finance?.entries) || [];
  const placedFix: Record<string, { month: string; why: string }> = {
    '19717': { month: '2024-01', why: 'advance, "Invoice PINV011614", posted 17 January 2024' },
    '19851': { month: '2024-06', why: 'advance, "Affiliate marketing - June W2+W3 2024"' },
    '20332': { month: '2026-06', why: 'June 2026 commission, no period in their extract' },
    '20331': { month: '2026-05', why: 'their extract labels it 04/2026; it bills May 2026' },
  };
  const billed = new Map<string, { amount: number; invoices: string[] }>();
  const placed: BillingTieOut['placed'] = [];
  const add = (month: string, invoice: string, amount: number) => {
    const g = billed.get(month) || { amount: 0, invoices: [] };
    g.amount += amount;
    g.invoices.push(invoice);
    billed.set(month, g);
  };
  for (const x of entries) {
    if (x.documentType !== 'Invoice') continue;
    const no = String(x.externalDocumentNo || x.documentNo || '').replace(/^PINV/, '');
    if (no === '20023') continue;
    const amount = -(Number(x.excludingFee) || 0);
    const m = String(x.description || '').match(/(\d{2})\/(\d{4})/);
    let month = m ? `${m[2]}-${m[1]}` : '';
    if (placedFix[no]) {
      month = placedFix[no].month;
      placed.push({ invoice: no, month, amount, why: placedFix[no].why });
    }
    if (!month) continue;
    add(month, no, amount);
  }
  if (!entries.some((x) => String(x.externalDocumentNo || '').endsWith('20317'))) {
    add('2026-04', '20317', SETTLEMENT_FACTS.invoice20317);
    placed.push({ invoice: '20317', month: '2026-04', amount: SETTLEMENT_FACTS.invoice20317, why: 'not in their extract; from Adwin\'s register' });
  }

  const reported = new Map<string, number>();
  for (const row of rows) for (const m of row.monthly) if (m.invoiced) reported.set(m.month, (reported.get(m.month) || 0) + m.invoiced);

  const monthsAll = [...new Set([...reported.keys(), ...billed.keys()])].filter((m) => m <= lastMonth).sort();
  let cumulative = 0;
  const months: BillingTieOutMonth[] = monthsAll.map((month) => {
    const rep = r2(reported.get(month) || 0);
    const b = r2(billed.get(month)?.amount || 0);
    const difference = r2(rep - b);
    cumulative = r2(cumulative + difference);
    return { month, reported: rep, billed: b, invoices: billed.get(month)?.invoices || [], difference, cumulative };
  });
  return {
    months,
    totalReported: r2(months.reduce((s, m) => s + m.reported, 0)),
    totalBilled: r2(months.reduce((s, m) => s + m.billed, 0)),
    neverBilled: r2(months.reduce((s, m) => s + m.difference, 0)),
    placed,
  };
}

export interface NeverBilledAllocation {
  /** Each account's share of the never-billed amount, always ≥ 0. */
  byAccount: Map<string, number>;
  /** Σ byAccount. Equals the tie-out's never-billed figure where the catch-up
   *  invoices never exceeded what was outstanding at the time. */
  allocated: number;
  /** The part of the head that falls on accounts whose report lines already
   *  exceed Raketech's figure — the only place a shortfalls-only scope could
   *  count the same money twice. */
  overlapWithOverpaid: number;
  overlapAccounts: { accountId: string; share: number; overpaid: number; overlap: number }[];
}

/**
 * Spreads the never-billed amount over accounts.
 *
 * The invoices that fell short of Raketech's reports were lump sums for a
 * month — "Publisher Costs 11/2023", 900,000 against 1,140,262 reported — and
 * the catch-ups were lump sums too ("Outstanding from error"). Neither names
 * an account, so each account's share is its share of the month's report.
 * Month by month: where less was billed than reported, every account active
 * in the month carries its proportional part of the shortfall; where more was
 * billed than reported, the excess pays down what accounts were carrying, in
 * proportion to their balances. What remains at the end is each account's
 * never-billed share, and its sum is the head.
 *
 * The one use made of the split is to find how much of the head falls on the
 * accounts a shortfalls-only scope has already set aside as over-invoiced,
 * so that part is not claimed twice.
 */
export function allocateNeverBilled(tieOut: BillingTieOut, rows: ClaimRow[]): NeverBilledAllocation {
  const pool = new Map<string, number>();
  const monthly = new Map<string, Map<string, number>>();
  for (const row of rows) for (const m of row.monthly) {
    if (m.invoiced <= 0) continue;
    if (!monthly.has(m.month)) monthly.set(m.month, new Map());
    monthly.get(m.month)!.set(row.accountId, m.invoiced);
  }
  for (const t of tieOut.months) {
    const accounts = monthly.get(t.month);
    if (!accounts || t.reported <= 0) continue;
    if (t.difference > 0) {
      // Under-billed: each account carries its proportional part.
      for (const [id, inv] of accounts) pool.set(id, (pool.get(id) || 0) + t.difference * (inv / t.reported));
    } else if (t.difference < 0) {
      // Over-billed: the excess pays down outstanding balances pro rata.
      const balance = [...pool.values()].reduce((s, v) => s + v, 0);
      if (balance <= 0) continue;
      const repay = Math.min(-t.difference, balance);
      for (const [id, v] of pool) pool.set(id, v - repay * (v / balance));
    }
  }
  const byAccount = new Map<string, number>();
  for (const [id, v] of pool) if (v > 0.005) byAccount.set(id, r2(v));
  const overlapAccounts: NeverBilledAllocation['overlapAccounts'] = [];
  for (const row of rows) {
    if (row.underpaid <= 1) continue;
    const share = byAccount.get(row.accountId) || 0;
    if (share <= 0) continue;
    const overlap = r2(Math.min(share, row.underpaid));
    overlapAccounts.push({ accountId: row.accountId, share, overpaid: r2(row.underpaid), overlap });
  }
  return {
    byAccount,
    allocated: r2([...byAccount.values()].reduce((s, v) => s + v, 0)),
    overlapWithOverpaid: r2(overlapAccounts.reduce((s, x) => s + x.overlap, 0)),
    overlapAccounts: overlapAccounts.sort((a, b) => b.overlap - a.overlap),
  };
}

/**
 * What is actually outstanding, in one column.
 *
 * Four things are kept apart because they answer to different documents. The
 * commission shortfall answers to the reconciliation. Commission Raketech
 * reported as earned and Adwin never billed answers to Raketech's monthly
 * reports set against its own ledger of Adwin's invoices. The flat fee answers
 * to clause 4.3.1 and cannot be reduced by anything in the reconciliation.
 * Unpaid invoices answer to themselves.
 */
export function buildOutstanding(options: OutstandingOptions): OutstandingStatement {
  const f = SETTLEMENT_FACTS;
  const lines: OutstandingLine[] = [];

  lines.push({
    label: 'Commission shortfall identified',
    amount: options.commissionShortfall,
    kind: 'claim',
    note: 'Earnings evidenced on Raketech\'s own figures, less what Adwin invoiced.',
  });
  // The claim measures earnings against Adwin's invoice line detail. Adwin
  // billed less than that detail, so the shortfall as computed is net of
  // invoices that were never actually raised.
  let billedRaw: number = f.commissionBilled;
  for (const [month, amount] of Object.entries(f.commissionBilledLater)) {
    if (options.invoicedThrough && month <= options.invoicedThrough) billedRaw += amount;
  }
  const billed = r2(billedRaw);
  const neverBilled = options.neverBilled !== undefined
    ? r2(Math.max(0, options.neverBilled))
    : options.claimInvoiced
      ? r2(Math.max(0, options.claimInvoiced - billed))
      : 0;
  const overlap = r2(Math.max(0, options.earningsBeyondInvoiceData || 0));
  if (overlap > 0) {
    lines.push({
      label: 'Less earnings already billed outside the detail',
      amount: -overlap,
      kind: 'credit',
      note: 'The last month of invoice line detail imported is earlier than the last month of their '
        + 'workbook, so those months carry earnings with nothing deducted against them. Invoices '
        + '20331 and 20332 bill the same months and were settled on 31 July 2026. This takes the '
        + 'overlap out so earnings already billed and paid are not claimed again. It comes back once '
        + 'the per-account detail for those months is imported.',
      provisional: true,
    });
  }

  lines.push({
    label: 'Less invoice 20023, already paid',
    amount: -f.settlementPaid,
    kind: 'credit',
    note: 'Paid 21 March 2025. An initial payment against the same historical discrepancy, '
      + 'so it comes off the same claim.',
  });
  const partPayment = options.creditedFeeLines === 'part_payment'
    ? f.feeLinesCreditedAsPartPayment
    : 0;
  if (partPayment > 0) {
    lines.push({
      label: 'Less fee lines Mar-Jun 2025, part payment of the underpayment',
      amount: -partPayment,
      kind: 'credit',
      note: 'Five lines on invoices 20033 to 20146, credited back on invoice 20318. Raketech '
        + 'proposed on 20 February 2025 to repay the commission error over twelve months from '
        + 'April, and stopped deducting transaction cost from May 2025 until the EUR '
        + `${f.transactionFeeToCompensate.toLocaleString('en-GB')} was compensated. These lines are `
        + 'that repayment, billed under the fee label. Money received against this claim, so it '
        + 'comes off it.',
    });
  }
  lines.push({
    label: 'Less "Reconciliation fee" lines already invoiced',
    amount: -f.reconciliationFeeInvoiced,
    kind: 'credit',
    note: 'Ten lines, Jul 2025 to Apr 2026. These bill the 5% Raketech agreed on 9 May 2025 not to '
      + 'retain, and the months from May 2025 are already valued gross of that 5% above, so leaving '
      + 'them in would count the same money twice. Adwin undertook in writing to credit them. They '
      + 'are compensation received against the transaction cost, and are credited on that head below.',
  });

  const commissionClaim = r2(
    options.commissionShortfall - overlap
    - f.settlementPaid - partPayment - f.reconciliationFeeInvoiced,
  );
  lines.push({ label: 'Commission claim', amount: commissionClaim, kind: 'subtotal' });

  // A head of its own, beside the shortfall and on the same footing as the
  // flat fee. The shortfall above measures Raketech's figures against what
  // Adwin invoiced on — Raketech's monthly reports. In a few months Adwin
  // billed less than those reports said: round advance invoices in October and
  // November 2023 that were only partly caught up in July 2024, and a short
  // invoice for September 2024. Raketech's own Finance ledger, which labels
  // every invoice with the period it bills, is the record of it, and from
  // October 2024 the two agree every month. What Raketech reported as earned
  // and was never billed for is due under clause 4.2, whether or not invoiced,
  // and clause 4.4 contemplates invoicing it later.
  if (neverBilled > 0) {
    lines.push({
      label: 'Reported by Raketech as earned, never billed by Adwin',
      amount: neverBilled,
      kind: 'entitlement',
      note: 'The shortfall above treats Raketech\'s monthly reports as what Adwin invoiced, because they '
        + `were what Adwin invoiced on. Adwin actually billed EUR ${billed.toLocaleString('en-GB')} of `
        + 'commission over the same ground — on its own register and on Raketech\'s Finance ledger, which '
        + 'agree — and the reports exceed that by this amount. Raketech\'s ledger places it month by month: '
        + 'EUR 900,000 billed for November 2023 against EUR 1,140,262 reported, caught up only in part in '
        + 'July 2024, and EUR 145,337 billed for September 2024 against EUR 187,144 reported. From October '
        + '2024 the two tie every month. Raketech\'s own report says it was earned; Raketech\'s own ledger '
        + 'says it was never billed. Clause 4.2 makes the fee due once payable by the operator, invoiced or '
        + 'not; clause 4.4 provides for invoicing it later.',
    });
  }
  // See `scopeNets`. On a shortfalls-only scope the part of this head that
  // falls on the accounts already set aside as invoiced above Raketech's
  // figure is taken out, so nothing is counted against Raketech twice.
  const setAside = options.scopeNets ? 0 : r2(Math.min(neverBilled, Math.max(0, options.neverBilledOverlap || 0)));
  if (setAside > 0) {
    lines.push({
      label: 'Less the part of it on accounts already set aside as invoiced above their figure',
      amount: -setAside,
      kind: 'credit',
      note: 'The shortfall above counts only the accounts Raketech underpaid, and sets aside the accounts '
        + 'where Adwin\'s report lines already exceed Raketech\'s figure. Each account\'s share of the '
        + 'never-billed amount is its share of the months concerned (the advances and catch-ups were lump '
        + `sums for a month, naming no account); on ${options.neverBilledOverlapAccounts ?? 'those'} of the `
        + 'set-aside accounts that share falls inside the excess already set aside, and is taken out here so '
        + 'it is not claimed twice. On the netted scopes both directions are already in the shortfall and this '
        + 'line does not arise.',
    });
  }
  const neverBilledCounted = r2(neverBilled - setAside);
  lines.push({ label: 'Never-billed commission outstanding', amount: neverBilledCounted, kind: 'subtotal' });

  // The flat fee is shown as contract amount less what was actually kept
  // against it, because "what is left" is the only figure that can be demanded
  // and the two halves are separately evidenced.
  lines.push({
    label: 'Flat fee due under clause 4.3.1',
    amount: f.flatFeeContract,
    kind: 'entitlement',
    note: `EUR ${f.flatFeeContract.toLocaleString('en-GB')} in the aggregate, payable within twelve `
      + `months of signature, so by ${f.flatFeeDueBy}. Clause 4.3 lists it alongside the Net `
      + 'Revenue, CPA and CPC fees, which makes it payable in addition to commission and not a '
      + 'component of it. It was agreed to cover the position before March 2023.',
  });
  lines.push({
    label: 'Less kept against it: fee lines Sep 2024 to Feb 2025',
    amount: -f.flatFeeRetained,
    kind: 'credit',
    note: `Six "Affiliate marketing fee" lines, invoices 19926 to 20017, worth EUR `
      + `${f.flatFeeRetained.toLocaleString('en-GB')}. Eleven such lines were billed in all, EUR `
      + `${f.flatFeeInvoiced.toLocaleString('en-GB')}, but the five from March 2025 were credited `
      + 'back on invoice 20318 and so never reached the flat fee. Whichever way those five are '
      + 'read, this balance is the same: either the flat fee never received them, or it received '
      + 'them and gave them straight back.',
  });

  const entitlements = r2(f.flatFeeContract - f.flatFeeRetained);
  lines.push({ label: 'Flat fee still outstanding', amount: entitlements, kind: 'subtotal' });

  // The transaction cost, on the same footing as the flat fee: a sum Raketech
  // undertook in writing to pay, less what has reached Adwin. What has reached
  // Adwin is the 5% Raketech stopped taking from May 2025 — 23,146.37 of it
  // billed as "Reconciliation fee" lines and paid, the rest inside the
  // commission shortfall above, which values those months gross on the
  // strength of the same undertaking. Deducting the whole waiver here is what
  // keeps the EUR 142,483 from being claimed twice.
  let transactionCost = 0;
  if (options.marginWaived !== undefined) {
    const waived = r2(Math.min(f.transactionFeeToCompensate, Math.max(0, options.marginWaived)));
    lines.push({
      label: 'Transaction cost Raketech undertook on 9 May 2025 to compensate',
      amount: f.transactionFeeToCompensate,
      kind: 'entitlement',
      note: 'Raketech deducted transaction and conversion cost from Adwin\'s commission through November '
        + '2024. On 9 May 2025 Johan Svensson confirmed in writing that from May 2025 Raketech would take '
        + `no fee until the full EUR ${f.transactionFeeToCompensate.toLocaleString('en-GB')} had been `
        + 'compensated, on new business and the historical accounts alike, and that any new cost would be '
        + 'discussed and agreed before it was applied. Raketech\'s own July 2026 report shows it applied the '
        + 'arrangement: 5% through April 2025, none from May 2025. Its letter of 9 August 2026 instead nets '
        + 'the same amount against commission it owed, and reserves a right to recharge cost from December '
        + '2024; neither is accepted. A sum agreed by Raketech\'s chief executive, payable in full.',
    });
    lines.push({
      label: `Less compensated through the 5% Raketech stopped taking, ${f.marginWaivedFrom} onward`,
      amount: -waived,
      kind: 'credit',
      note: `5% of Raketech's own gross figures for May 2025 to the cutoff. EUR `
        + `${f.reconciliationFeeInvoiced.toLocaleString('en-GB', { minimumFractionDigits: 2 })} of it was billed `
        + 'as "Reconciliation fee" lines and paid, and is deducted from the commission claim above for that '
        + 'reason; the rest sits inside the commission shortfall, which values those months gross because '
        + 'of the same undertaking. The whole waiver comes off here so that the compensation is claimed '
        + 'once: part through the shortfall, the balance on this head.',
    });
    transactionCost = r2(f.transactionFeeToCompensate - waived);
    lines.push({ label: 'Transaction cost compensation outstanding', amount: transactionCost, kind: 'subtotal' });
  }

  lines.push({
    label: 'Invoices issued and unpaid',
    amount: f.unpaidInvoices,
    kind: 'invoice',
    note: `None. Invoices ${f.invoicesSettled.invoices} were settled on ${f.invoicesSettled.on} in one `
      + `transfer of EUR ${f.invoicesSettled.amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })}, `
      + 'referenced "SETT/ INV 20331 / INV 20332". Invoice 20318 is excluded because it is this claim, '
      + 'not an addition to it.',
  });

  const total = r2(commissionClaim + neverBilledCounted + entitlements + transactionCost + f.unpaidInvoices);
  lines.push({ label: 'Total outstanding', amount: total, kind: 'total' });

  return { lines, commissionClaim, neverBilled: neverBilledCounted, entitlements, transactionCost, unpaidInvoices: f.unpaidInvoices, total };
}
